import {
  BackSide,
  BoxGeometry,
  Color,
  DynamicDrawUsage,
  Group,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshStandardNodeMaterial,
  type BufferGeometry,
  type Scene,
} from 'three/webgpu';
import { requestLatePrecompile } from './renderer.js';
import { attribute, color } from 'three/tsl';
import { lerp } from '@stillsun/shared';
import type { LootContainerDef, Vec3 } from '@stillsun/shared';
import {
  CRATE_SHAPES,
  CRATE_VARIANTS,
  buildCrateGeometry,
  crateMaterials,
  crateVariant,
  type CrateGeometry,
  type CrateVariant,
} from './crates.js';

/**
 * The loot locker field.
 *
 * Every locker is an *instance slot* in a shared batch, not a mesh of its own: 44 lockers
 * used to be 220 `Mesh`es and are a handful of batches whatever the count.
 *
 * Two things changed with `render/crates.ts`:
 *
 *  - **Three silhouettes, not one.** `LootContainerDef.table` has carried
 *    `crate_common` / `crate_hull` / `crate_hymn` since M2 and this field ignored it, so a
 *    player could not tell an 8-in-the-map Hymn locker from a common one until they were
 *    standing on it. Lockers are now bucketed by table and each bucket gets its own batches.
 *  - **The lid hinges again.** The generated crate GLB was one unposed watertight mesh with
 *    no lid, so an opened locker could only settle 2 cm on its springs. The procedural
 *    crates are authored as body + lid about a real hinge line, so looting a container opens
 *    it — and the lid instance is just another matrix, so it stays inside the batch.
 *
 * The seam is still the tier read at distance (amber for settler tables, Hymn white for the
 * Hymn table) and still dims when the server reports the locker looted.
 */

/** Emissive of a sealed seam vs one the server has already reported the contents of. */
const SEAM_SEALED = 5;
const SEAM_OPENED = 0.6;
/** Per-instance seam brightness, uploaded as an instanced geometry attribute. */
const SEAM_GLOW_ATTRIBUTE = 'seamGlow';

const AMBER = 0xffa040;
/** Seam width as a fraction of the crate's footprint, and its drop below the lid line. */
const SEAM_WIDTH_FRAC = 0.66;
const SEAM_DROP = 0.055;

export interface NearContainer {
  id: number;
  dist: number;
  pos: Vec3;
}

/** One locker. `variant` picks its bucket; `index` is its row inside that bucket's batches. */
interface Container {
  id: number;
  pos: Vec3;
  variant: CrateVariant;
  index: number;
  opened: boolean;
  /** 0 = shut, 1 = fully open. Eased so `markOpened` is not a pop. */
  openT: number;
}

/** The batches for one variant, plus the geometry they were built from. */
interface Bucket {
  readonly variant: CrateVariant;
  readonly geo: CrateGeometry;
  readonly body: InstancedMesh;
  readonly bodyTrim: InstancedMesh | null;
  readonly lid: InstancedMesh;
  readonly seam: InstancedMesh;
  readonly seamGeo: BufferGeometry;
  readonly seamGlow: InstancedBufferAttribute;
  /** Lockers in this bucket, in `index` order. */
  readonly members: Container[];
}

/**
 * One geometry set per variant, built on first use and kept for the process lifetime. The
 * geometry is shared across every field, so a map change re-instances but never re-merges.
 */
const geoCache = new Map<CrateVariant, CrateGeometry>();
function crateGeometry(v: CrateVariant): CrateGeometry {
  let g = geoCache.get(v);
  if (!g) {
    g = buildCrateGeometry(v);
    geoCache.set(v, g);
  }
  return g;
}

/**
 * Seam materials, one per variant so the Hymn locker's cold-white line and the settler
 * tables' amber are different *materials* rather than a per-instance colour — there are only
 * three of them, they are built once, and the alternative is a second instanced attribute on
 * a mesh that already carries one.
 */
let seamMats: Map<CrateVariant, MeshStandardNodeMaterial> | null = null;
function seamMaterials(): Map<CrateVariant, MeshStandardNodeMaterial> {
  if (seamMats) return seamMats;
  const out = new Map<CrateVariant, MeshStandardNodeMaterial>();
  for (const v of CRATE_VARIANTS) {
    const hex = CRATE_SHAPES[v].seamColor;
    const m = new MeshStandardNodeMaterial({
      color: 0x2a1b10,
      emissive: new Color(hex),
      emissiveIntensity: SEAM_SEALED,
      roughness: 0.5,
      metalness: 0,
    });
    try {
      // Per-instance glow: 44 lockers dim independently inside one draw call. Fails soft to
      // a fixed sealed brightness — a locker that does not dim beats one that does not draw.
      m.emissiveNode = color(hex).mul(attribute(SEAM_GLOW_ATTRIBUTE, 'float'));
    } catch (err) {
      console.warn('[stillsun] per-instance seam glow unavailable, seams stay sealed-bright', err);
    }
    out.set(v, m);
  }
  seamMats = out;
  return out;
}

/** The one highlight shell, shared by the whole field. */
let shellMat: MeshStandardNodeMaterial | null = null;
function shellMaterial(): MeshStandardNodeMaterial {
  shellMat ??= new MeshStandardNodeMaterial({
    color: 0x000000,
    emissive: new Color(AMBER),
    emissiveIntensity: 3,
    side: BackSide,
    depthWrite: false,
    roughness: 1,
    metalness: 0,
  });
  return shellMat;
}

const SCRATCH = new Matrix4();
const HINGE = new Matrix4();

export class ContainerField {
  private readonly scene: Scene;
  private readonly root = new Group();
  private readonly containers = new Map<number, Container>();
  private readonly buckets = new Map<CrateVariant, Bucket>();
  private highlightId = 0;
  private clock = 0;

  /** Back-facing unit box, rescaled onto whichever locker is highlighted. */
  private shell: Mesh | null = null;
  private shellGeo: BoxGeometry | null = null;

  constructor(scene: Scene) {
    this.scene = scene;
    this.root.name = 'containers';
    this.scene.add(this.root);
  }

  /** `undefined` is the normal case in the yard — only the Spine ships containers. */
  setContainers(list: readonly LootContainerDef[] | undefined): void {
    // Idempotent for an unchanged set, the same rule `ExtractBeacons.setExtracts` follows.
    //
    // This is called twice per raid — once from the hub's deploy and again from the server's
    // Welcome, which is authoritative about the map. Rebuilding on the second call threw away
    // batches the deploy's `precompileScene` had just warmed and built fresh `InstancedMesh`es
    // that needed their own pipelines, which then compiled on the first frame a locker was
    // looked at. Keeping the existing batches is both cheaper and the thing that holds the
    // zero-new-pipelines gate.
    if (this.sameSet(list)) return;
    this.clear();
    if (!list || list.length === 0) return;

    // Bucket by table first: each variant's batch has to be sized to its own count.
    const byVariant = new Map<CrateVariant, Container[]>();
    for (const def of list) {
      const variant = crateVariant(def.table);
      const c: Container = {
        id: def.id,
        pos: { ...def.pos },
        variant,
        index: 0,
        opened: false,
        openT: 0,
      };
      let members = byVariant.get(variant);
      if (!members) {
        members = [];
        byVariant.set(variant, members);
      }
      c.index = members.length;
      members.push(c);
      this.containers.set(def.id, c);
    }

    const mats = crateMaterials();
    const seams = seamMaterials();
    for (const [variant, members] of byVariant) {
      const geo = crateGeometry(variant);
      const n = members.length;
      const shape = CRATE_SHAPES[variant];

      const body = new InstancedMesh(geo.body, mats.body[variant], n);
      body.name = `crate:body:${variant}`;
      const lid = new InstancedMesh(geo.lid, mats.body[variant], n);
      lid.name = `crate:lid:${variant}`;
      const bodyTrim = geo.bodyTrim ? new InstancedMesh(geo.bodyTrim, mats.trim, n) : null;
      if (bodyTrim) bodyTrim.name = `crate:trim:${variant}`;
      for (const mesh of [body, lid, bodyTrim]) {
        if (!mesh) continue;
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.instanceMatrix.setUsage(DynamicDrawUsage);
        // Never culled — the same rule (and the same reason) as `render/decals.ts`.
        // `precompileScene` walks the scene through the *world camera's* frustum, and a
        // batch culled at that moment is never compiled; the Spine's 44 lockers then built
        // their pipelines on the first frame one came into view. Measured: 20 mid-raid
        // pipelines named `crate:*`. Three to eleven always-drawn batches is the cheaper side
        // of that trade.
        mesh.frustumCulled = false;
      }

      // A private clone of the seam bar, so the per-instance glow attribute belongs to this
      // bucket's batch and not to a shared singleton.
      const seamGeo = new BoxGeometry(shape.w * SEAM_WIDTH_FRAC, 0.035, 0.03);
      const glow = new InstancedBufferAttribute(new Float32Array(n).fill(SEAM_SEALED), 1);
      glow.setUsage(DynamicDrawUsage);
      seamGeo.setAttribute(SEAM_GLOW_ATTRIBUTE, glow);
      const seamMat = seams.get(variant);
      if (!seamMat) continue;
      const seam = new InstancedMesh(seamGeo, seamMat, n);
      seam.name = `crate:seam:${variant}`;
      seam.castShadow = false;
      seam.receiveShadow = false;
      seam.frustumCulled = false;
      seam.instanceMatrix.setUsage(DynamicDrawUsage);

      const bucket: Bucket = {
        variant,
        geo,
        body,
        bodyTrim,
        lid,
        seam,
        seamGeo,
        seamGlow: glow,
        members,
      };
      this.buckets.set(variant, bucket);
      this.root.add(body, lid, seam);
      if (bodyTrim) this.root.add(bodyTrim);
    }

    // Unit box: the shell is scaled onto each locker's own footprint in `fitShell`.
    this.shellGeo = new BoxGeometry(1, 1, 1);
    this.shell = new Mesh(this.shellGeo, shellMaterial());
    this.shell.name = 'crate:highlight';
    this.shell.castShadow = false;
    this.shell.receiveShadow = false;
    this.shell.visible = false;
    this.root.add(this.shell);

    this.writeInstances();
    // New materials in the scene after the deploy precompile: warm them before they draw.
    requestLatePrecompile('containers');
  }

  /**
   * True when `list` describes exactly the field already standing: same ids, same order, same
   * table. Position is not compared — the server never moves a locker mid-raid, and the ids
   * are generated from the map definition, so an id match is a position match.
   */
  private sameSet(list: readonly LootContainerDef[] | undefined): boolean {
    const n = list?.length ?? 0;
    if (n !== this.containers.size) return false;
    if (n === 0) return true;
    for (const def of list ?? []) {
      const have = this.containers.get(def.id);
      if (!have || have.variant !== crateVariant(def.table)) return false;
    }
    return true;
  }

  /** Rewrites every instance matrix and seam glow. A few dozen rows — cheap to redo whole. */
  private writeInstances(): void {
    for (const b of this.buckets.values()) {
      const shape = CRATE_SHAPES[b.variant];
      for (const c of b.members) {
        const i = c.index;
        const { x, y, z } = c.pos;

        // Body geometry is authored with its origin on the ground at the locker's centre,
        // so the body instance is a plain translation.
        b.body.setMatrixAt(i, SCRATCH.makeTranslation(x, y, z));
        b.bodyTrim?.setMatrixAt(i, SCRATCH);

        // The lid is authored about its hinge, so opening it is a rotation about the hinge
        // followed by the same translation — the whole open state, inside the batch.
        SCRATCH.makeRotationX(shape.openRad * c.openT);
        HINGE.makeTranslation(x, y + b.geo.hingeY, z + b.geo.hingeZ);
        b.lid.setMatrixAt(i, SCRATCH.multiplyMatrices(HINGE, SCRATCH));

        // Seam on the front face along the lid line — the one edge a standing player sees.
        b.seam.setMatrixAt(
          i,
          SCRATCH.makeTranslation(
            x,
            y + shape.h - shape.lidH - SEAM_DROP,
            z - (shape.d / 2 + 0.02),
          ),
        );
        b.seamGlow.setX(i, lerp(SEAM_SEALED, SEAM_OPENED, c.openT));
      }
      for (const mesh of [b.body, b.bodyTrim, b.lid, b.seam]) {
        if (!mesh) continue;
        mesh.instanceMatrix.needsUpdate = true;
        mesh.computeBoundingSphere();
      }
      b.seamGlow.needsUpdate = true;
    }
  }

  update(dt: number): void {
    this.clock += dt;
    shellMaterial().emissiveIntensity = 3 + 2 * Math.sin(this.clock * Math.PI * 3); // 1.5 Hz

    let moved = false;
    for (const c of this.containers.values()) {
      const target = c.opened ? 1 : 0;
      if (c.openT === target) continue;
      // ~0.35 s to settle; fast enough to feel like a response to the interact.
      c.openT =
        Math.abs(target - c.openT) < 0.01 ? target : lerp(c.openT, target, Math.min(1, dt * 8));
      moved = true;
    }
    if (moved) this.writeInstances();
  }

  markOpened(id: number): void {
    const c = this.containers.get(id);
    if (c) c.opened = true;
  }

  setHighlight(id: number): void {
    if (id === this.highlightId) return;
    this.highlightId = id;
    this.fitShell(this.containers.get(id));
  }

  /** Parks the one outline mesh on `c`, or hides it when nothing is highlighted. */
  private fitShell(c: Container | undefined): void {
    const shell = this.shell;
    if (!shell) return;
    if (!c) {
      shell.visible = false;
      return;
    }
    const s = CRATE_SHAPES[c.variant];
    shell.scale.set(s.w * 1.06, s.h * 1.06, s.d * 1.06);
    shell.position.set(c.pos.x, c.pos.y + s.h / 2, c.pos.z);
    shell.visible = true;
  }

  /**
   * Nearest container within range, opened or not — whether an already-looted locker is
   * still worth prompting for is the caller's call, not this file's.
   */
  nearest(from: Vec3, maxDist: number): NearContainer | null {
    let best: NearContainer | null = null;
    for (const c of this.containers.values()) {
      const d = Math.hypot(c.pos.x - from.x, c.pos.y - from.y, c.pos.z - from.z);
      if (d > maxDist) continue;
      if (best && d >= best.dist) continue;
      best = { id: c.id, dist: d, pos: { x: c.pos.x, y: c.pos.y, z: c.pos.z } };
    }
    return best;
  }

  /** Triangles and draw calls this field contributes, for the perf report. */
  stats(): { triangles: number; draws: number; lockers: number } {
    let triangles = 0;
    let draws = 0;
    for (const b of this.buckets.values()) {
      draws += 3 + (b.bodyTrim ? 1 : 0);
      triangles += b.geo.triangles * b.members.length;
    }
    return { triangles, draws, lockers: this.containers.size };
  }

  clear(): void {
    for (const b of this.buckets.values()) {
      for (const mesh of [b.body, b.bodyTrim, b.lid, b.seam]) {
        if (!mesh) continue;
        this.root.remove(mesh);
        mesh.dispose();
      }
      // The seam bar is the only field-owned geometry (a per-bucket clone, so it can carry
      // the instanced glow attribute); the crate geometry itself is cached and shared.
      b.seamGeo.dispose();
    }
    this.buckets.clear();
    if (this.shell) this.root.remove(this.shell);
    this.shellGeo?.dispose();
    this.shell = null;
    this.shellGeo = null;
    this.containers.clear();
    this.highlightId = 0;
  }
}

/**
 * Three stand-in lockers around the yard origin — one of each table, so `?debug=raid` shows
 * all three silhouettes side by side without the Spine map. Ids sit well below the 40000
 * entity band and above the player id range, so they cannot be confused with either.
 */
export function debugContainers(groundY: number): LootContainerDef[] {
  return [
    { id: 9001, pos: { x: 4.5, y: groundY, z: -6.5 }, table: 'crate_common' },
    { id: 9002, pos: { x: -6.0, y: groundY, z: 1.5 }, table: 'crate_hull' },
    { id: 9003, pos: { x: 1.0, y: groundY, z: 9.0 }, table: 'crate_hymn' },
  ];
}
